import { researcherService } from "@/services/cluster/researcher.service/researcher.service";
import { clusterApiService } from "@/services/cluster/cluster-api.service/cluster-api.service";
import { workloadServiceApi } from "@/services/infra/client-apis/workloads-service-api/workloads-service-api";
import { httpService } from "@/services/infra/https.service/http.service";
import { inferenceService } from "@/services/control-plane/inference.service/inference.service";
import { assetsServiceApi } from "@/services/infra/client-apis/assets-service-api/assets-service-api";

import type { AxiosResponse } from "axios";
import type {
  WorkloadMetricType,
  MetricsResponse,
  Pod,
  PodMetricType,
  PodVerbosity,
  BatchResponse,
  WorkloadDetailed,
  PendingSchedulingMessage,
  UnifiedWorkload,
  UnifiedWorkloadRequest,
} from "@/swagger-models/workloads-client";
import { WorkloadSortFilterFields } from "@/swagger-models/workloads-client";
import { API, RESEARCHER } from "@/common/api.constant";
import type {
  IWorkloadMetadata,
  IWorkloadSpec,
  IWorkload,
  IWorkloadResponse,
  EWorkloadTelemetryGroupBy,
  ICanSubmitWorkload,
} from "@/models/workload.model";
import { EWorkloadType } from "@/models/workload.model";

import { ForbiddenError, HttpErrorResponse, NotFoundError, ServiceUnavailableError } from "@/models/http-response.model";
import type { IFilterBy, IPaginationFilter } from "@/models/filter.model";
import { controlPlaneService } from "@/services/control-plane/control-plane.service/control-plane.service";
import type {
  GetWorkloadHistory200Response,
  TelemetryResponse,
  Workload,
  WorkloadPatchFields,
  WorkloadTelemetryType,
} from "@/swagger-models/workloads-client";
import { httpResponseService } from "@/services/infra/http-response.service/http-response.service";
import { Phase } from "@/swagger-models/workloads-client";
import { workloadLocalPhaseUpdater } from "@/services/cluster/workload.service/local-status-updater";
import { filterService } from "@/services/filter.service/filter.service";
import { isNewerVersion } from "@/utils/version.util";
import { MIN_INFERENCE_AUTO_DELETE, MIN_WORKLOAD_CREATION_V2_VERSION } from "@/common/version.constant";
import { fileService, FileType } from "@/services/file.service/file.service";
import { orgUnitService } from "@/services/control-plane/org-unit.service/org-unit.service";
import type { IReadinessStatus } from "@/models/researcher.model";
import { ClusterDisplayedStatusStateEnum, type DisplayedCluster } from "@/swagger-models/cluster-service-client";
import { whoamiService } from "@/services/cluster/whoami.service/whoami.service";

export const workloadService = {
  list,
  activate,
  getWorkloadPods,
  getWorkloadHistory,
  getWorkloadById,
  getWorkloadsCount,
  getWorkloads,
  handleFailedWorkloadClusterCreation,
  getWorkloadsTelemetry,
  getWorkloadsTelemetryCsv,
  countWorkloadsByName,
  getWorkloadMetrics,
  listPods,
  countPods,
  patchWorkloadById,
  getWorkloadPodMetrics,
  getWorkloadPendingSchedulingMessages,
  getUnifiedWorkload,
  canSubmitWorkload,
  // Create
  createWorkload,
  // Delete
  deleteWorkload,
  deleteWorkloadV2,
  deleteMultipleWorkloads,
  // Stop
  stopWorkload,
  stopWorkloadV2,
  stopMultipleWorkloads,
  // Activate
  activateWorkloadV2,
  activateMultipleWorkloads,
};

const WORKLOADS_RESEARCHER_ENDPOINT = `${RESEARCHER.v1}/workload/proxy`;
const apiVersion = "run.ai/v2alpha1";
const WORKLOADS_SERVICE_ENDPOINT = `${API.v1}/workloads`;
const WORKLOADS_TELEMETRY_ENDPOINT = `${API.v1}/workloads/telemetry`;

interface IErrorResponseData {
  name: string;
  ok: boolean;
  error?: {
    message: string;
    details: string;
    status: number;
  };
}
interface IErrorResponse {
  data: IErrorResponseData[];
}

// api calls
async function createWorkload(
  kind: string,
  metadata: IWorkloadMetadata,
  spec: IWorkloadSpec,
  masterSpec?: IWorkloadSpec,
): Promise<IWorkloadResponse> {
  const workload: IWorkload = {
    apiVersion,
    kind,
    metadata,
    spec,
    masterSpec,
  };

  try {
    const workloadResponse = await clusterApiService.post(
      `${WORKLOADS_RESEARCHER_ENDPOINT}/namespaces/${metadata.namespace}/${kind}`,
      workload,
    );
    return workloadResponse;
  } catch (error: unknown) {
    if (error instanceof HttpErrorResponse) {
      switch (error.statusCode) {
        case 401:
          error.message = "The API server is not configured correctly. Contact your administrator.";
          break;
        case 502:
        case 504:
          error.message =
            "There are issues with your connection to the cluster. Make sure you're using your organization's VPN.";
          break;
        case 400:
          if (error.message.startsWith("A workload with the same URL already exists")) {
            break;
          }
        // don't break on purpose! continue to default case
        /* falls through */
        default:
          error.message = "There are issues with your connection to the cluster. Contact your administrator.";
      }
    }
    throw error;
  }
}

async function getWorkloads(
  deleted?: boolean,
  offset?: number,
  limit?: number,
  sortOrder?: "asc" | "desc" | undefined,
  sortBy?: string,
  filterBy?: string[],
  search?: string,
): Promise<Workload[]> {
  const response = await workloadServiceApi.workloadsApi.getWorkloads(
    deleted,
    offset,
    limit,
    sortOrder,
    sortBy,
    filterBy,
    search,
  );
  return response.data.workloads;
}

async function list(clusterUuid: string, filterBy: IFilterBy = {}, deleted = false): Promise<Workload[]> {
  const validFilterFields = [...Object.values(WorkloadSortFilterFields)];
  filterBy.clusterUuid = clusterUuid;
  const filters: IPaginationFilter = filterService.mapColumnsFilterToFilterParams(filterBy, validFilterFields, true);
  const workloads = await getWorkloads(
    deleted,
    filters.offset,
    filters.limit,
    filters.sortOrder,
    filters.sortBy,
    filters.filterBy,
    filters.search,
  );
  return workloadLocalPhaseUpdater.updateWorkloadsByLocalPhases(workloads);
}

async function getWorkloadsCount(
  filterBy?: Array<string>,
  deleted = false,
  search?: string,
  precise?: boolean,
): Promise<number> {
  const response = await workloadServiceApi.workloadsApi.countWorkloads(deleted, filterBy, search, precise);
  return response.data.count;
}

const _isWorkloadProjectExists = async (workload: Workload): Promise<boolean> => {
  try {
    await orgUnitService.getProject(workload.projectId);
    return true;
  } catch (e) {
    return false;
  }
};

const _has404Error = (response: IErrorResponse): boolean => {
  if (!response || !response.data || !response.data.length) {
    return false;
  }

  return response.data.some((item: IErrorResponseData) => item.error && item.error.status === NotFoundError.statusCode);
};

const _has503Error = (response: IErrorResponse): boolean => {
  if (!response || !response.data || !response.data.length) {
    return false;
  }

  return response.data.some(
    (item: IErrorResponseData) => item.error && item.error.status === ServiceUnavailableError.statusCode,
  );
};

function _has207WithErrors(response: IErrorResponse): boolean {
  if (!response || !response.data || !response.data.length) {
    return false;
  }

  return response.data.some((item: IErrorResponseData) => item.error && item.error.status >= 400);
}

async function deleteMultipleWorkloads(workloadIds: Array<string>): Promise<BatchResponse> {
  return (await workloadServiceApi.workloadsBatchApi.batchWorkloads({ ids: workloadIds, action: "delete" })).data;
}

async function deleteWorkload(workload: Workload, project: string, clusterVersion: string): Promise<void> {
  let response;

  if (workload.type === EWorkloadType.Inference) {
    response = await inferenceService.remove(workload.name, workload.namespace);

    if (!isNewerVersion(clusterVersion, MIN_INFERENCE_AUTO_DELETE)) {
      response = await assetsServiceApi.inferenceApi.deleteInferenceById(workload.id);
    }
  } else {
    response = await researcherService.deleteJob(workload.name, project, workload.type);
  }

  if (_has503Error(response)) {
    // Attempt to bypass workload ownership protection
    throw new ServiceUnavailableError("Operation not allowed");
  }

  if (_has404Error(response) || !(await _isWorkloadProjectExists(workload))) {
    //in this case workload not deleted successfully in the cluster
    await workloadService.patchWorkloadById(workload.id, {
      phase: Phase.Deleting,
      softDelete: true,
    });
  }

  if (_has207WithErrors(response)) {
    if (response.data[0].error.status === ForbiddenError.statusCode) {
      throw new ForbiddenError();
    }
  }
}

async function deleteWorkloadV2(workloadId: string, workloadType: string): Promise<void> {
  try {
    switch (workloadType) {
      case EWorkloadType.Distributed:
        return (await workloadServiceApi.distributedApi.deleteDistributed(workloadId)).data;
      case EWorkloadType.Training:
        return (await workloadServiceApi.trainingsApi.deleteTraining(workloadId)).data;
      case EWorkloadType.Workspace:
        return (await workloadServiceApi.workspacesApi.deleteWorkspace(workloadId)).data;
      case EWorkloadType.Inference:
        return (await workloadServiceApi.inferencesApi.deleteInference(workloadId)).data;
    }
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

async function stopMultipleWorkloads(workloadIds: Array<string>): Promise<BatchResponse> {
  return (await workloadServiceApi.workloadsBatchApi.batchWorkloads({ ids: workloadIds, action: "suspend" })).data;
}

async function stopWorkloadV2(workload: Workload): Promise<void> {
  try {
    switch (workload.type) {
      case EWorkloadType.Training:
        (await workloadServiceApi.trainingsApi.suspendTraining(workload.id)).data;
        break;
      case EWorkloadType.Workspace:
        (await workloadServiceApi.workspacesApi.suspendWorkspace(workload.id)).data;
        break;
      case EWorkloadType.Distributed:
        (await workloadServiceApi.distributedApi.suspendDistributed(workload.id)).data;
        break;
    }
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

async function activateMultipleWorkloads(workloadIds: Array<string>): Promise<BatchResponse> {
  return (await workloadServiceApi.workloadsBatchApi.batchWorkloads({ ids: workloadIds, action: "resume" })).data;
}

async function activateWorkloadV2(workload: Workload): Promise<void> {
  try {
    switch (workload.type) {
      case EWorkloadType.Training:
        (await workloadServiceApi.trainingsApi.resumeTraining(workload.id)).data;
        break;
      case EWorkloadType.Workspace:
        (await workloadServiceApi.workspacesApi.resumeWorkspace(workload.id)).data;
        break;
      case EWorkloadType.Distributed:
        (await workloadServiceApi.distributedApi.resumeDistributed(workload.id)).data;
        break;
    }
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

async function stopWorkload(workload: Workload): Promise<void> {
  if (!workload.projectName) throw new Error("Can't stop workload. Job is missing.");
  const res = await researcherService.stopWorkload(workload.name as string, workload.projectName);
  const errorMessage: string | undefined = res.data[0]?.error ? res.data[0].error.details : undefined;
  if (errorMessage) {
    throw httpResponseService.getError(res.data[0]?.code, errorMessage);
  }
}

async function activate(workload: Workload): Promise<void> {
  const res = await researcherService.activateWorkload(workload.name as string, workload.projectName);
  workloadLocalPhaseUpdater.updateLocalPhases(workload, Phase.Resuming);
  const errorMessage: string | undefined = res.data[0]?.error ? res.data[0].error.details : undefined;
  if (errorMessage) {
    throw httpResponseService.getError(res.data[0]?.code, errorMessage);
  }
}

async function getWorkloadPods(
  workloadId: string,
  deleted?: boolean,
  offset?: number,
  limit?: number,
  search?: string,
): Promise<Array<Pod>> {
  const response = await workloadServiceApi.podsApi.getWorkloadPods(workloadId, deleted, offset, limit, search);
  return response.data.pods;
}
async function listPods(
  deleted?: boolean,
  offset?: number,
  limit?: number,
  sortOrder?: "asc" | "desc",
  sortBy?: "clusterId" | "workloadId" | "nodeName" | "currentNodepool" | "createdAt" | "completedAt" | "deletedAt",
  filterBy?: Array<string>,
  verbosity?: PodVerbosity,
  completed?: string,
  search?: string,
): Promise<Array<Pod>> {
  const response = await workloadServiceApi.podsApi.listPods(
    deleted,
    offset,
    limit,
    sortOrder,
    sortBy,
    filterBy,
    verbosity,
    completed,
    search,
  );
  return response.data.pods;
}

async function countPods(deleted?: boolean, filterBy?: Array<string>, search?: string): Promise<number> {
  const response = await workloadServiceApi.podsApi.countPods(deleted, filterBy, search);
  return response.data.count;
}

async function getWorkloadById(workloadId: string): Promise<WorkloadDetailed> {
  return (await workloadServiceApi.workloadsApi.getWorkload(workloadId)).data;
}

async function patchWorkloadById(workloadId: string, patchFields: WorkloadPatchFields): Promise<Workload> {
  return controlPlaneService.patch(`${WORKLOADS_SERVICE_ENDPOINT}/${workloadId}`, patchFields);
}

async function getWorkloadHistory(workloadId: string, clusterUuid: string): Promise<GetWorkloadHistory200Response> {
  return controlPlaneService.get(`${WORKLOADS_SERVICE_ENDPOINT}/${workloadId}/history?clusterId=${clusterUuid}`);
}

async function countWorkloadsByName(name: string, clusterId: string, projectId?: number | null): Promise<number> {
  try {
    let filterBy = `name==${name},clusterId==${clusterId}`;
    if (Number.isFinite(projectId)) {
      filterBy = `${filterBy},projectId==${projectId}`;
    }

    const res = await workloadServiceApi.workloadsApi.countWorkloads(false, [filterBy], undefined, true);
    return res.data.count;
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

// pending scheduling messages
async function getWorkloadPendingSchedulingMessages(workloadId: string): Promise<PendingSchedulingMessage[]> {
  try {
    const workload = await getWorkloadById(workloadId);
    return workload.pendingSchedulingMessages || [];
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

// new metrics apis
async function getWorkloadMetrics(
  workloadId: string,
  start: string,
  end: string,
  metricType: Array<WorkloadMetricType>,
  numberOfSamples?: number,
): Promise<MetricsResponse> {
  try {
    const res: AxiosResponse<MetricsResponse> = await workloadServiceApi.workloadsApi.getWorkloadMetrics(
      workloadId,
      metricType,
      start,
      end,
      numberOfSamples,
    );
    return res.data;
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

async function handleFailedWorkloadClusterCreation(workloadId: string, e: unknown): Promise<void> {
  try {
    let phaseMessage = "failed to create workload in cluster";
    if (e instanceof HttpErrorResponse) {
      phaseMessage = e.message;
    }
    await patchWorkloadById(workloadId, {
      phase: Phase.Failed,
      phaseMessage,
      softDelete: true,
    });
  } catch (e) {
    console.error("Failed to patch workload phase", e);
  }
}

async function getWorkloadPodMetrics(
  workloadId: string,
  podId: string,
  metricType: Array<PodMetricType>,
  start: string,
  end: string,
  numberOfSamples?: number,
): Promise<MetricsResponse> {
  try {
    const res: AxiosResponse<MetricsResponse> = await workloadServiceApi.podsApi.getWorkloadPodMetrics(
      workloadId,
      podId,
      metricType,
      start,
      end,
      numberOfSamples,
    );
    return res.data;
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

//telemetry
async function getWorkloadsTelemetry(
  telemetryType: WorkloadTelemetryType,
  clusterId?: string,
  nodepoolName?: string,
  departmentId?: string,
  groupBy?: Array<EWorkloadTelemetryGroupBy>,
): Promise<TelemetryResponse> {
  try {
    const response = await workloadServiceApi.workloadsApi.getWorkloadsTelemetry(
      telemetryType,
      clusterId,
      nodepoolName,
      departmentId,
      groupBy,
    );
    return response.data;
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

//telemetry csv
async function getWorkloadsTelemetryCsv(
  telemetryType: WorkloadTelemetryType,
  clusterId?: string,
  nodepoolName?: string,
  departmentId?: string,
  groupBy?: Array<EWorkloadTelemetryGroupBy>,
): Promise<void> {
  try {
    const query: Record<string, string | number | Array<string>> = {
      telemetryType: telemetryType,
    };
    if (clusterId) {
      query["clusterId"] = clusterId;
    }
    if (nodepoolName) {
      query["nodepoolName"] = nodepoolName;
    }
    if (departmentId) {
      query["departmentId"] = departmentId;
    }
    if (groupBy && groupBy.length > 0) {
      query["groupBy"] = groupBy;
    }
    await fileService.downloadFile(WORKLOADS_TELEMETRY_ENDPOINT, FileType.csv, undefined, query, {});
  } catch (err: unknown) {
    throw httpService.handleHttpError(err);
  }
}

async function getUnifiedWorkload(unifiedRequest: UnifiedWorkloadRequest): Promise<UnifiedWorkload> {
  const response = await workloadServiceApi.unifiedApi.getUnifiedWorkload(unifiedRequest);
  return response.data;
}

/**
 * Determines whether a workload can be submitted to the specified cluster.
 *
 * @param {DisplayedCluster} cluster - The cluster to check for workload submission.
 * @returns {Promise<ICanSubmitWorkload | null>}
 *   - `null` if the cluster is available for workload submission.
 *   - An object with `info` if submission is not possible and the reason.
 *   - The object may also contain `statusCode` if the `whoami` request fails.
 */

async function canSubmitWorkload(cluster: DisplayedCluster): Promise<ICanSubmitWorkload | null> {
  if (!cluster.version || isNewerVersion(cluster.version, MIN_WORKLOAD_CREATION_V2_VERSION)) {
    const isClusterConnected =
      cluster.status?.state !== ClusterDisplayedStatusStateEnum.Disconnected &&
      cluster.status?.state !== ClusterDisplayedStatusStateEnum.WaitingToConnect;

    if (isClusterConnected) return null;
  }

  if (!cluster.domain) {
    return { info: "cluster domain is missing or empty" };
  }

  const readinessStatus: IReadinessStatus = await whoamiService.isCurrentClusterConnected(cluster.domain);
  if (!readinessStatus.connected) {
    return { info: "whoami request failed", statusCode: readinessStatus.statusCode };
  }

  return null;
}
